/**
 * La banda de filtros de Novedades: qué se está por consultar y si lo que está
 * en pantalla todavía corresponde.
 *
 * Vive aparte de la vista porque es la lógica que decide **qué período y qué
 * choferes** se liquidan. Un sello que dice "al día" cuando los controles ya
 * se movieron lleva a impactar números de otro período, y eso no se ve hasta
 * que está en un recibo.
 */

import type { RespuestaConsulta, TipoChofer } from '../../api';

/** El orden en que se muestran y se mandan, para que no dependa del clic. */
export const ORDEN_CHOFERES: readonly TipoChofer[] = ['PROPIO', 'EVENTUAL'];

/**
 * Prende o apaga un tipo de chofer. **Nunca deja la selección vacía**: sin
 * choferes no hay consulta, y un botón apagado sin motivo se lee como una
 * pantalla rota. Destildar el último no hace nada.
 */
export function alternarChofer(
  tipos: ReadonlySet<TipoChofer>,
  tipo: TipoChofer,
): ReadonlySet<TipoChofer> {
  if (tipos.has(tipo)) {
    if (tipos.size === 1) return tipos;
    const proximos = new Set(tipos);
    proximos.delete(tipo);
    return proximos;
  }
  return new Set([...tipos, tipo]);
}

/** Si lo consultado y lo que está tildado en la banda son la misma población. */
export function mismosChoferes(
  consultados: readonly TipoChofer[],
  tildados: ReadonlySet<TipoChofer>,
): boolean {
  return consultados.length === tildados.size && consultados.every((t) => tildados.has(t));
}

/**
 * El primer y el último día del mes, en ISO.
 *
 * Se arma con las partes y no con `Date.parse`: el texto se interpreta como
 * UTC y corre el día (§ Fechas de PATRONES-UI).
 */
export function limitesDelMes(anio: number, mes: number): { primerDia: string; ultimoDia: string } {
  const p = (n: number): string => String(n).padStart(2, '0');
  // Día 0 del mes siguiente es el último del mes, sin tabla de días.
  const ultimo = new Date(anio, mes, 0).getDate();
  return { primerDia: `${String(anio)}-${p(mes)}-01`, ultimoDia: `${String(anio)}-${p(mes)}-${p(ultimo)}` };
}

/** Un mes para adelante o para atrás, cruzando el año. */
export function moverMes(anio: number, mes: number, pasos: number): { anio: number; mes: number } {
  // Día 1 a propósito: con el día de hoy, el 31/03 pasa a "31/02" y JavaScript
  // lo corre al 03/03.
  const d = new Date(anio, mes - 1 + pasos, 1);
  return { anio: d.getFullYear(), mes: d.getMonth() + 1 };
}

/** El período elegido ya terminó, que es cuando se liquida. */
export function esMesVencido(anio: number, mes: number, hoy: Date = new Date()): boolean {
  const inicioDelSiguiente = new Date(anio, mes, 1);
  return inicioDelSiguiente.getTime() <= hoy.getTime();
}

/** Lo que hay tildado en la banda, para compararlo con lo consultado. */
export interface ControlesBanda {
  readonly anio: number;
  readonly mes: number;
  readonly tipos: ReadonlySet<TipoChofer>;
  /** Intervalo de los eventuales. Vacío si no se consultan. */
  readonly intervalo: { readonly desde: string; readonly hasta: string };
  /** Fecha de los conceptos manuales. Vacía si no se cargó. */
  readonly manual: { readonly desde: string; readonly hasta: string };
}

/**
 * Los controles ya no describen lo que está en pantalla.
 *
 * Cambiar la operación **no** entra acá: eso descarta la consulta, así que no
 * hay nada desactualizado que señalar.
 */
export function estaDesactualizado(
  consulta: RespuestaConsulta | null,
  controles: ControlesBanda,
): boolean {
  if (consulta === null) return false;

  if (Number(consulta.periodo.slice(0, 4)) !== controles.anio) return true;
  if (Number(consulta.periodo.slice(5, 7)) !== controles.mes) return true;
  if (!mismosChoferes(consulta.tiposChofer, controles.tipos)) return true;

  if ((consulta.intervalo?.desde ?? '') !== controles.intervalo.desde) return true;
  if ((consulta.intervalo?.hasta ?? '') !== controles.intervalo.hasta) return true;
  if ((consulta.rangoManual?.desde ?? '') !== controles.manual.desde) return true;
  if ((consulta.rangoManual?.hasta ?? '') !== controles.manual.hasta) return true;

  return false;
}

export type TonoSello = 'ok' | 'atencion' | 'error' | 'neutro';

export interface Sello {
  readonly tono: TonoSello;
  readonly texto: string;
}

/**
 * De qué son los números que están en pantalla, en una línea.
 *
 * Es lo único que separa "esto es lo que voy a impactar" de "esto es de otra
 * consulta", así que el estado desactualizado se dice con todas las letras y
 * no con un matiz de color.
 */
export function selloDe(entrada: {
  readonly hayOperaciones: boolean;
  readonly consulta: RespuestaConsulta | null;
  readonly desactualizado: boolean;
  readonly hora: string;
  readonly periodoLegible: (consulta: RespuestaConsulta) => string;
}): Sello {
  if (!entrada.hayOperaciones) {
    return { tono: 'error', texto: 'No hay conexión configurada a ningún TMS' };
  }
  if (entrada.consulta === null) return { tono: 'neutro', texto: 'Sin consulta' };

  if (entrada.desactualizado) {
    return {
      tono: 'atencion',
      // Sin punto después de la hora: en es-AR termina en "p. m." y queda
      // un doble punto que se lee como un error de la pantalla.
      texto: `En pantalla, lo consultado ${entrada.hora} · cambiaste filtros, consultá de nuevo`,
    };
  }
  return {
    tono: 'ok',
    texto: `Consultado ${entrada.hora} · ${entrada.periodoLegible(entrada.consulta)}`,
  };
}
